<template>
  <b-modal
    size="lg"
    hide-footer
    id="deployment-modal"
    title="Deployment Info 🚀"
  >
    <dl class="deployment-info">
      <div v-for="row in rows" :key="row.label">
        <dt>{{ row.label }}</dt>
        <dd>
          <a v-if="row.href" :href="row.href" target="_blank" rel="noopener">{{
            row.value
          }}</a>
          <pre v-else-if="row.pre" class="deployment-pre">{{ row.value }}</pre>
          <span v-else>{{ row.value }}</span>
        </dd>
      </div>
    </dl>
  </b-modal>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface DeploymentEnv {
  DEV?: boolean;
  VITE_COMMIT_SHA?: string;
  VITE_COMMIT_MESSAGE?: string;
  VITE_COMMIT_AUTHOR?: string;
  VITE_COMMIT_TIMESTAMP?: string;
  VITE_COMMIT_BRANCH?: string;
  VITE_COMMIT_TAG?: string;
  VITE_PIPELINE_ID?: string;
  VITE_PIPELINE_URL?: string;
  VITE_JOB_NAME?: string;
  VITE_PROJECT_URL?: string;
}

const env = (import.meta as unknown as { env: DeploymentEnv }).env;

function formatTimestamp(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
}

const rows = computed(() => {
  const sha = env.VITE_COMMIT_SHA;
  const projectUrl = env.VITE_PROJECT_URL;
  const pipelineId = env.VITE_PIPELINE_ID;
  const pipelineUrl = env.VITE_PIPELINE_URL;
  const tag = env.VITE_COMMIT_TAG;
  const firstMessageLine = env.VITE_COMMIT_MESSAGE?.split('\n')[0] || '—';

  return [
    {
      label: 'Commit',
      value: sha || '—',
      href: sha && projectUrl ? `${projectUrl}/-/commit/${sha}` : undefined
    },
    {
      label: 'Branch',
      value: env.VITE_COMMIT_BRANCH || '—'
    },
    ...(tag
      ? [
          {
            label: 'Tag',
            value: tag,
            href: projectUrl ? `${projectUrl}/-/tags/${tag}` : undefined
          }
        ]
      : []),
    {
      label: 'Author',
      value: env.VITE_COMMIT_AUTHOR || '—'
    },
    {
      label: 'Timestamp',
      value: formatTimestamp(env.VITE_COMMIT_TIMESTAMP)
    },
    {
      label: 'Message',
      value: firstMessageLine,
      pre: true
    },
    {
      label: 'Pipeline',
      value: pipelineId || '—',
      href: pipelineId && pipelineUrl ? pipelineUrl : undefined
    },
    {
      label: 'Job',
      value: env.VITE_JOB_NAME || '—'
    }
  ];
});
</script>

<style scoped>
.deployment-info {
  display: grid;
  grid-template-columns: 7.5rem 1fr;
  gap: 0.375rem 0.75rem;

  & > div {
    display: contents;
  }
}
dt {
  font-weight: 600;
  color: #6c757d;
  align-self: start;
  padding-top: 0.125rem;
}
dd {
  margin: 0;
  word-break: break-all;
}
.deployment-pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.8rem;
  background: #f8f9fa;
  padding: 0.25rem 0.375rem;
  border-radius: 0.25rem;
}
</style>
